import { createRoot } from 'react-dom/client';
import App from './App';
import './App.css';
import './index.css';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import Dashboard from './Components/Dashboard/Dashboard';
import { Provider } from 'react-redux';
import { store } from './Reducers/store';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import { fetchCurrentUser, setUserFromToken } from './Actions/index';
import { isAfter, toDate } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import UserProfile from './Components/User/UserProfile';
import EditUserProfile from './Components/User/EditUserProfile';
import UserList from './Components/User/UserList';
import Login from './Components/User/Login';
import RequireAuth from './Components/Auth/RequireAuth';
import ErrorPage from './Components/Common/ErrorPage';
import { DecodedToken } from './Models/DecodedToken';
import ResetPassword from './Components/User/ResetPassword';
import ForgotPassword from './Components/User/ForgotPassword';
import ManufacturerList from './Components/Manufacturer/ManufacturerList';
import ManufacturerProfile from './Components/Manufacturer/ManufacturerProfile';
import EditManufacturer from './Components/Manufacturer/EditManufacturer';
import EquipmentList from './Components/Equipment/EquipmentList';
import EquipmentProfile from './Components/Equipment/EquipmentProfile';
import EditEquipment from './Components/Equipment/EditEquipment';
import Display from './Components/Products/Display';
import AboutUs from './Components/About/AboutUs';
import Contact from './Components/About/Contact';
import ManufacturersDisplay from './Components/Products/ManufacturersDisplay';
import SearchPage from './Components/Products/SearchPage';
import SearchPageParts from './Components/Products/PartsSearchPage';
import ContactFormList from './Components/ContactForm/ContactFormList';
import { EquipmentTypeEnums } from './Models/EquipmentTypeEnums';
import SuperDashboard from './Components/Dashboard/SuperDashboard';
import RequestQuoteList from './Components/ReuestQuoteForm/RequestQuoteFormList';
import CategoriesDisplay from './Components/Products/CategoriesDisplay';
import ViewOrder from './Components/Order/ViewOrder';
import ClientList from './Components/Client/ClientList';
import RegisterUser from './Components/User/RegisterUser';
import WarrantyClaim from './Components/Warranty/WarrantyClaim';
import WarrantyList from './Components/Warranty/WarrantyList';
import WarrantyMyClaims from './Components/Warranty/WarrantyMyClaims';
import ManualsDisplay from './Components/Manuals/ManualDisplay';
import ManualList from './Components/Manuals/ManualList';
import DealerLocatorMap from './Components/DealerLocator/DealerLocatorMap';
import OrderCheckout from './Components/Order/OrderCheckout';
import OrderReview from './Components/Order/OrderReview';
import OrderConfirmation from './Components/Order/OrderConfirmation';
import OrderList from './Components/Order/OrderList';
import OrderListAdmin from './Components/Order/Admin/OrderList';
import ClientDetail from './Components/Client/ClientDetail';
import OrderDetail from './Components/Order/Admin/OrderDetail';
import ManufacturerSearchPage from './Components/Products/ManufacturerSearchPage';
import ManualPage from './Components/Manuals/ManualPage';
import SitePages from './Components/Common/SitePages';
import OldManualListPage from './Components/Manuals/OldManualList';
import OldManualEdit from './Components/Manuals/OldManualEdit';

const router = createBrowserRouter([
  {
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: <Dashboard />,
      },
      {
        path: '/dashboard',
        element: (
          <RequireAuth>
            <SuperDashboard />
          </RequireAuth>
        ),
      },
      {
        element: <Login />,
        path: '/Login',
      },
      {
        element: <RegisterUser />,
        path: '/register',
      },
      {
        element: <ForgotPassword />,
        path: '/forgotpassword',
      },
      {
        element: <ResetPassword />,
        path: '/resetpassword/:guid',
      },
      {
        element: <ResetPassword />,
        path: '/resetpassword',
      },
      {
        element: <AboutUs />,
        path: '/about',
      },
      {
        element: <Contact />,
        path: '/contact',
      },
      {
        element: <DealerLocatorMap />,
        path: '/dealerlocator',
      },
      {
        element: <SitePages />,
        path: '/siteresources',
      },
      {
        path: '/clients',
        element: (
          <RequireAuth>
            <ClientList />
          </RequireAuth>
        ),
      },
      {
        path: '/client/:id',
        element: (
          <RequireAuth>
            <ClientDetail />
          </RequireAuth>
        ),
      },
      {
        path: '/users',
        element: (
          <RequireAuth>
            <UserList />
          </RequireAuth>
        ),
      },
      {
        path: '/user/:id',
        element: (
          <RequireAuth>
            <UserProfile />
          </RequireAuth>
        ),
      },
      {
        path: '/edit/user/:id',
        element: (
          <RequireAuth>
            <EditUserProfile />
          </RequireAuth>
        ),
      },
      {
        path: '/manufacturers',
        element: (
          <RequireAuth>
            <ManufacturerList />
          </RequireAuth>
        ),
      },
      {
        path: '/manufacturer/:id',
        element: (
          <RequireAuth>
            <ManufacturerProfile />
          </RequireAuth>
        ),
      },
      {
        path: '/edit/manufacturer/:id',
        element: (
          <RequireAuth>
            <EditManufacturer />
          </RequireAuth>
        ),
      },
      {
        path: '/equipmentlist',
        element: (
          <RequireAuth>
            <EquipmentList
              manufacturerId={0}
              equipmentType={EquipmentTypeEnums.Equipment}
            />
          </RequireAuth>
        ),
      },
      {
        path: '/equipment/:id',
        element: (
          <RequireAuth>
            <EquipmentProfile equipmentType={EquipmentTypeEnums.Equipment} />
          </RequireAuth>
        ),
      },
      {
        path: '/edit/equipment/:id',
        element: (
          <RequireAuth>
            <EditEquipment equipmentType={EquipmentTypeEnums.Equipment} />
          </RequireAuth>
        ),
      },
      {
        path: '/Wholegoods/:id',
        element: (
          <RequireAuth>
            <EquipmentProfile equipmentType={EquipmentTypeEnums.Equipment} />
          </RequireAuth>
        ),
      },
      {
        path: '/edit/Wholegoods/:id',
        element: (
          <RequireAuth>
            <EditEquipment equipmentType={EquipmentTypeEnums.Equipment} />
          </RequireAuth>
        ),
      },
      {
        path: '/partslist',
        element: (
          <RequireAuth>
            <EquipmentList
              manufacturerId={0}
              equipmentType={EquipmentTypeEnums.Parts}
            />
          </RequireAuth>
        ),
      },
      {
        path: '/parts/:id',
        element: (
          <RequireAuth>
            <EquipmentProfile equipmentType={EquipmentTypeEnums.Parts} />
          </RequireAuth>
        ),
      },
      {
        path: '/edit/parts/:id',
        element: (
          <RequireAuth>
            <EditEquipment equipmentType={EquipmentTypeEnums.Parts} />
          </RequireAuth>
        ),
      },
      {
        path: '/warrantylist',
        element: (
          <RequireAuth>
            <WarrantyList />
          </RequireAuth>
        ),
      },
      {
        path: '/products/equipment/:id',
        element: <Display />,
      },
      {
        path: '/products/manufacturers',
        element: <ManufacturersDisplay />,
      },
      {
        path: '/manufacturer/:id/products',
        element: <ManufacturerSearchPage />,
      },
      {
        path: '/manufacturer/:id/manuals',
        element: <ManualPage />,
      },
      {
        path: '/products/categories',
        element: <CategoriesDisplay />,
      },
      {
        path: '/products/search',
        element: <SearchPage />,
      },
      {
        path: '/products/search/parts',
        element: <SearchPageParts />,
      },
      {
        path: '/orders/view',
        element: (
          <RequireAuth>
            <ViewOrder />
          </RequireAuth>
        ),
      },
      {
        path: '/orders/checkout',
        element: (
          <RequireAuth>
            <OrderCheckout />
          </RequireAuth>
        ),
      },
      {
        path: '/orders/review',
        element: (
          <RequireAuth>
            <OrderReview />
          </RequireAuth>
        ),
      },
      {
        path: '/orders/confirm',
        element: (
          <RequireAuth>
            <OrderConfirmation />
          </RequireAuth>
        ),
      },
      {
        path: '/orders/list',
        element: (
          <RequireAuth>
            <OrderList />
          </RequireAuth>
        ),
      },
      {
        path: '/admin/order/list',
        element: (
          <RequireAuth>
            <OrderListAdmin />
          </RequireAuth>
        ),
      },
      {
        path: '/admin/order/:id/detail',
        element: (
          <RequireAuth>
            <OrderDetail />
          </RequireAuth>
        ),
      },
      {
        path: '/contactforms',
        element: (
          <RequireAuth>
            <ContactFormList />
          </RequireAuth>
        ),
      },
      {
        path: '/requestQuotes',
        element: (
          <RequireAuth>
            <RequestQuoteList />
          </RequireAuth>
        ),
      },
      {
        path: '/warranty/claim/:id?',
        element: <WarrantyClaim />,
      },
      {
        path: '/warranty/myclaims',
        element: (
          <RequireAuth>
            <WarrantyMyClaims />
          </RequireAuth>
        ),
      },
      {
        path: '/manuals',
        element: <ManualsDisplay />,
      },
      {
        path: '/manuallist',
        element: (
          <RequireAuth>
            <OldManualListPage />
            {/* <ManualList /> */}
          </RequireAuth>
        ),
      },
      {
        path: '/oldmanuallist',
        element: (
          <RequireAuth>
            <OldManualListPage />
          </RequireAuth>
        ),
      },
    ],
  },
]);

const token = localStorage.getItem('PriceBrosToken');
if (token) {
  const decoded: DecodedToken = jwtDecode(token);
  const now = new Date();
  store.dispatch(
    setUserFromToken({
      email:
        decoded[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
        ],
      exp: decoded.exp,
    })
  );
  const isValid = isAfter(toDate(decoded.exp * 1000), now);
  if (isValid) {
    store.dispatch(fetchCurrentUser());
  }
}

createRoot(document.getElementById('root')!).render(
  <Provider store={store}>
    {/* <React.StrictMode> */}
    <RouterProvider router={router} />
    <ToastContainer
      position='bottom-center'
      autoClose={2000}
      limit={3}
      closeOnClick
      theme='dark'
    />
    {/* <App /> */}
    {/* </React.StrictMode> */}
  </Provider>
);
